import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Upload } from "lucide-react";
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { getWellsideBySlug, updateWellside, createWellside, updateGallery, uploadGalleryImage, uploadImage, formatApiErrorDetail, listShippingRates } from "@/admin/adminApi";
import { GalleryManager } from "@/admin/GalleryManager";
import { ImageCropModal } from "@/admin/ImageCropModal";
import { useImageCrop } from "@/admin/useImageCrop";
import { WELLSIDE_FEATURES } from "@/data/wellsides";
import { FocalPointPicker } from "@/admin/FocalPointPicker";
import { OptionGroupsSection } from "@/admin/product-edit-sections/OptionGroupsSection";
import { FeaturesSection } from "@/admin/product-edit-sections/FeaturesSection";
import { SpecsSection } from "@/admin/product-edit-sections/SpecsSection";
import { ContentSectionsSection } from "@/admin/product-edit-sections/ContentSectionsSection";

const FIELD_CLASS = "w-full mt-1.5 bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-white focus:border-[var(--brand-primary)] outline-none";
const LABEL_CLASS = "text-xs font-mono uppercase tracking-widest text-white/60";

const slugify = (str) => str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminWellsideEditPage() {
  const { slug: slugParam } = useParams();
  const isNew = slugParam === "new";
  const navigate = useNavigate();
  const [mongoId, setMongoId] = useState(null); // resolved from the slug once loaded — used for all mutating API calls
  const [slugTouched, setSlugTouched] = useState(false);
  const [form, setForm] = useState({
    slug: "", name: "", variants: "", price: "", priceFrom: false, priceOnRequest: false,
    availability: "", tagline: "", thumb: "", intro: "", features: [],
    specs: [], contentSections: [],
    seoTitle: "", seoDescription: "", optionGroups: [], variantStock: [],
    shippingRateId: "",
  });
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [error, setError] = useState("");
  const [usingDefaultFeatures, setUsingDefaultFeatures] = useState(false);
  const [newFeature, setNewFeature] = useState("");
  const [newSpecKey, setNewSpecKey] = useState("");
  const [newSpecValue, setNewSpecValue] = useState("");
  const [shippingRates, setShippingRates] = useState([]);

  useEffect(() => { listShippingRates().then(setShippingRates); }, []);

  const optionalShippingRates = useMemo(() => shippingRates.filter((r) => !r.isDefault), [shippingRates]);
  const heroPickerGallery = useMemo(() => gallery.filter((g) => g.type !== "video"), [gallery]);

  useEffect(() => {
    if (isNew) return;
    getWellsideBySlug(slugParam).then((data) => {
      const hasCustomFeatures = Array.isArray(data.features) && data.features.length > 0;
      setMongoId(data.id);
      setForm({
        slug: data.slug, name: data.name, variants: data.variants || "", price: data.price ?? "",
        priceFrom: !!data.priceFrom, priceOnRequest: !!data.priceOnRequest,
        availability: data.availability || "", tagline: data.tagline || "", thumb: data.thumb,
        hero: data.hero || "",
        intro: data.intro || "", features: hasCustomFeatures ? data.features : [...WELLSIDE_FEATURES],
        specs: data.specs || [], contentSections: data.contentSections || [],
        seoTitle: data.seoTitle || "", seoDescription: data.seoDescription || "",
        optionGroups: data.optionGroups || [], variantStock: data.variantStock || [],
        shippingRateId: data.shippingRateId || "",
        heroFocalMobile: data.heroFocalMobile || "",
        heroPosition: data.heroPosition || "",
      });
      setUsingDefaultFeatures(!hasCustomFeatures);
      setGallery(data.gallery || []);
      setLoading(false);
    });
  }, [slugParam, isNew]);

  const handleField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleNameChange = (value) => {
    setForm((f) => ({ ...f, name: value, slug: slugTouched ? f.slug : slugify(value) }));
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: slugify(value) }));
  };

  const handleGalleryChange = async (newGallery) => {
    setGallery(newGallery);
    await updateGallery(mongoId, newGallery);
  };

  const handleSetHero = async (src) => {
    setForm((f) => ({ ...f, hero: src }));
    await updateWellside(mongoId, { hero: src });
  };

  const handleSetHeroFocal = async (val) => {
    setForm((f) => ({ ...f, heroFocalMobile: val }));
    await updateWellside(mongoId, { heroFocalMobile: val });
  };

  const handleSetHeroPosition = async (val) => {
    setForm((f) => ({ ...f, heroPosition: val }));
    await updateWellside(mongoId, { heroPosition: val });
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const { gallery: updated } = await uploadGalleryImage(mongoId, file);
      setGallery(updated);
      return updated;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleThumbUpload = async (file) => {
    setThumbUploading(true);
    setError("");
    try {
      const { url } = await uploadImage(file);
      handleField("thumb", url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setThumbUploading(false);
    }
  };
  const thumbCrop = useImageCrop(handleThumbUpload);

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    if (isNew && !form.thumb) {
      setError("Please upload a main photo before creating the product.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const created = await createWellside({
          slug: form.slug, name: form.name, variants: form.variants || null,
          price: form.price === "" ? 0 : Number(form.price), priceFrom: form.priceFrom, priceOnRequest: form.priceOnRequest,
          availability: form.availability || null, tagline: form.tagline || null,
          thumb: form.thumb, intro: form.intro || null, features: form.features,
          shippingRateId: form.shippingRateId || null,
        });
        navigate(`/admin/wellside-canopies/${created.slug}`);
      } else {
        await updateWellside(mongoId, {
          name: form.name, variants: form.variants || null,
          price: form.price === "" ? null : Number(form.price), priceFrom: form.priceFrom, priceOnRequest: form.priceOnRequest,
          availability: form.availability || null, tagline: form.tagline || null,
          intro: form.intro || null, optionGroups: form.optionGroups, variantStock: form.variantStock,
          specs: form.specs, contentSections: form.contentSections,
          seoTitle: form.seoTitle || null, seoDescription: form.seoDescription || null,
          shippingRateId: form.shippingRateId || null,
          // Only persist a per-product features override if the admin actually edited the
          // list — otherwise this product should keep inheriting the shared default list.
          ...(usingDefaultFeatures ? {} : { features: form.features }),
        });
        navigate("/admin/wellside-canopies");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setForm((f) => ({ ...f, features: [...f.features, newFeature.trim()] }));
    setNewFeature("");
    setUsingDefaultFeatures(false);
  };
  const removeFeature = (i) => {
    setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
    setUsingDefaultFeatures(false);
  };
  const updateFeature = (i, value) => {
    setForm((f) => ({ ...f, features: f.features.map((feat, idx) => (idx === i ? value : feat)) }));
    setUsingDefaultFeatures(false);
  };

  const addSpec = () => {
    if (!newSpecKey.trim() || !newSpecValue.trim()) return;
    setForm((f) => ({ ...f, specs: [...f.specs, [newSpecKey.trim(), newSpecValue.trim()]] }));
    setNewSpecKey("");
    setNewSpecValue("");
  };
  const removeSpec = (i) => setForm((f) => ({ ...f, specs: f.specs.filter((_, idx) => idx !== i) }));
  const updateSpec = (i, col, value) => setForm((f) => ({
    ...f,
    specs: f.specs.map((row, idx) => (idx === i ? (col === 0 ? [value, row[1]] : [row[0], value]) : row)),
  }));

  const addContentSection = () => setForm((f) => ({ ...f, contentSections: [...f.contentSections, { heading: "", body: "", bullets: [] }] }));
  const removeContentSection = (si) => setForm((f) => ({ ...f, contentSections: f.contentSections.filter((_, i) => i !== si) }));
  const updateContentSection = (si, key, value) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, [key]: value } : s)),
  }));
  const moveContentSection = (si, dir) => setForm((f) => {
    const newIndex = si + dir;
    if (newIndex < 0 || newIndex >= f.contentSections.length) return f;
    const sections = [...f.contentSections];
    [sections[si], sections[newIndex]] = [sections[newIndex], sections[si]];
    return { ...f, contentSections: sections };
  });
  const addSectionBullet = (si) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, bullets: [...s.bullets, ""] } : s)),
  }));
  const removeSectionBullet = (si, bi) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, bullets: s.bullets.filter((_, j) => j !== bi) } : s)),
  }));
  const updateSectionBullet = (si, bi, value) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, bullets: s.bullets.map((b, j) => (j === bi ? value : b)) } : s)),
  }));

  const addOptionGroup = () => {
    setForm((f) => ({
      ...f,
      optionGroups: [...f.optionGroups, { key: `group-${f.optionGroups.length + 1}`, label: "", required: true, choices: [] }],
    }));
  };
  const removeOptionGroup = (gi) => setForm((f) => ({ ...f, optionGroups: f.optionGroups.filter((_, i) => i !== gi) }));
  const updateOptionGroup = (gi, key, value) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, [key]: value, ...(key === "label" ? { key: slugify(value) || g.key } : {}) } : g)),
  }));
  const addChoice = (gi) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: [...g.choices, { label: "", priceDelta: 0, inStock: true }] } : g)),
  }));
  const removeChoice = (gi, ci) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: g.choices.filter((_, j) => j !== ci) } : g)),
  }));
  const updateChoice = (gi, ci, key, value) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: g.choices.map((c, j) => (j === ci ? { ...c, [key]: value } : c)) } : g)),
  }));
  const optionGroupSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleOptionGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(String(active.id).split("-").pop());
    const newIndex = Number(String(over.id).split("-").pop());
    setForm((f) => ({ ...f, optionGroups: arrayMove(f.optionGroups, oldIndex, newIndex) }));
  };
  const handleChoiceDragEnd = (gi) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(String(active.id).split("-").pop());
    const newIndex = Number(String(over.id).split("-").pop());
    setForm((f) => ({
      ...f,
      optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: arrayMove(g.choices, oldIndex, newIndex) } : g)),
    }));
  };

  // Full cartesian product of every group's choices — e.g. Colour x Glass —
  // used to let the admin toggle stock per *combination*, since a per-choice
  // inStock flag can't express "Black is in stock with or without glass, but
  // Dark Grey is only in stock with glass".
  const combinations = form.optionGroups
    .filter((g) => g.choices.length > 0)
    .reduce((acc, group) => {
      const rows = group.choices.map((c) => ({ [group.key]: c.label }));
      return acc.length === 0 ? rows : acc.flatMap((combo) => rows.map((row) => ({ ...combo, ...row })));
    }, []);
  const findComboEntry = (combo) => form.variantStock.find((v) => {
    const keys = Object.keys(v.combination);
    return keys.length === Object.keys(combo).length && keys.every((k) => v.combination[k] === combo[k]);
  });
  const toggleComboStock = (combo, checked) => setForm((f) => {
    const existing = findComboEntry(combo);
    if (!existing) return { ...f, variantStock: [...f.variantStock, { combination: combo, inStock: checked }] };
    return { ...f, variantStock: f.variantStock.map((v) => (v === existing ? { ...v, inStock: checked } : v)) };
  });

  if (loading) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-wellside-edit-page" className="max-w-3xl">
      <Link to="/admin/wellside-canopies" data-testid="admin-edit-back-link" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6">
        <ArrowLeft size={14} /> Back to list
      </Link>
      <h1 className="font-display text-3xl text-white mb-6">{isNew ? "Add Wellside Canopy" : `Edit — ${form.name}`}</h1>

      {error && <p data-testid="admin-edit-error" className="text-red-400 text-sm mb-4 font-mono">{error}</p>}

      <form onSubmit={handleSave} className="space-y-6">
        <label className="block">
          <span className={LABEL_CLASS}>Name</span>
          <input data-testid="admin-field-name" required value={form.name} onChange={(e) => handleNameChange(e.target.value)} className={FIELD_CLASS} />
        </label>
        {isNew && (
          <label className="block">
            <span className={LABEL_CLASS}>Slug (URL path, unique)</span>
            <input data-testid="admin-field-slug" required value={form.slug} onChange={(e) => handleSlugChange(e.target.value)} className={FIELD_CLASS} placeholder="auto-generated from name — e.g. toyota-hilux-2026" />
            {form.slug && <p className="text-white/40 text-xs mt-1.5">Product page will be: /wellside-canopies/{form.slug}</p>}
          </label>
        )}
        <label className="block">
          <span className={LABEL_CLASS}>Variants</span>
          <input data-testid="admin-field-variants" value={form.variants} onChange={(e) => handleField("variants", e.target.value)} className={FIELD_CLASS} />
        </label>
        <label className="block">
          <span className={LABEL_CLASS}>Tagline</span>
          <input data-testid="admin-field-tagline" value={form.tagline} onChange={(e) => handleField("tagline", e.target.value)} className={FIELD_CLASS} />
        </label>
        {isNew && (
          <div>
            <span className={LABEL_CLASS}>Main Photo</span>
            <div className="mt-2 flex items-center gap-4">
              {form.thumb && (
                <img src={form.thumb} alt="Main product photo preview" data-testid="admin-thumb-preview" className="w-24 h-24 object-cover border border-[var(--border-default)]" />
              )}
              <label className="inline-flex items-center gap-2 btn-secondary cursor-pointer" data-testid="admin-thumb-upload-label">
                <Upload size={14} /> {thumbUploading ? "Uploading…" : form.thumb ? "Replace Photo" : "Upload Photo"}
                <input
                  type="file"
                  accept="image/*"
                  data-testid="admin-thumb-upload-input"
                  className="hidden"
                  disabled={thumbUploading}
                  onChange={(e) => {
                    if (e.target.files?.[0]) thumbCrop.selectFile(e.target.files[0]);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            {!form.thumb && <p className="text-white/40 text-xs mt-1.5">A main photo is required before creating the product.</p>}
          </div>
        )}
        <ImageCropModal
          open={thumbCrop.isOpen}
          imageSrc={thumbCrop.imageSrc}
          fileName={thumbCrop.fileName}
          onCancel={thumbCrop.cancel}
          onConfirm={thumbCrop.confirm}
        />
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className={LABEL_CLASS}>Price (NZD) {form.optionGroups.length > 0 && <span className="text-white/40 normal-case">— base price before option add-ons</span>}</span>
            <input data-testid="admin-field-price" type="number" step="0.01" value={form.price} onChange={(e) => handleField("price", e.target.value)} className={FIELD_CLASS} disabled={form.priceOnRequest} />
          </label>
        </div>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-white/80 text-sm">
            <input data-testid="admin-field-price-from" type="checkbox" checked={form.priceFrom} onChange={(e) => handleField("priceFrom", e.target.checked)} />
            Price is "From" (starting price)
          </label>
          <label className="flex items-center gap-2 text-white/80 text-sm">
            <input data-testid="admin-field-price-on-request" type="checkbox" checked={form.priceOnRequest} onChange={(e) => handleField("priceOnRequest", e.target.checked)} />
            Price on request (Enquire only)
          </label>
        </div>
        <label className="block">
          <span className={LABEL_CLASS}>Shipping Rate</span>
          <select
            data-testid="admin-field-shipping-rate"
            value={form.shippingRateId}
            onChange={(e) => handleField("shippingRateId", e.target.value)}
            className={FIELD_CLASS}
          >
            <option value="">
              {shippingRates.find((r) => r.isDefault) ? `Default ($${(shippingRates.find((r) => r.isDefault).priceCents / 100).toFixed(2)})` : "Default"}
            </option>
            {optionalShippingRates.map((r) => (
              <option key={r.id} value={r.id}>{r.name} (${(r.priceCents / 100).toFixed(2)})</option>
            ))}
          </select>
        </label>
        <p className="text-white/40 text-xs -mt-2">
          "In Stock (allow Buy Now)" and "Badge" (image overlay label) are managed from the Wellside Canopies list page — set them there.
        </p>
        <label className="block">
          <span className={LABEL_CLASS}>Availability text</span>
          <input data-testid="admin-field-availability" value={form.availability} onChange={(e) => handleField("availability", e.target.value)} className={FIELD_CLASS} placeholder="e.g. In stock, ships in 3-5 days" />
        </label>
        <label className="block">
          <span className={LABEL_CLASS}>Intro / Description</span>
          <textarea data-testid="admin-field-intro" rows={4} value={form.intro} onChange={(e) => handleField("intro", e.target.value)} className={FIELD_CLASS} />
        </label>

        {!isNew && (
          <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)]/40 p-4 space-y-4">
            <p className="text-white/70 text-sm font-semibold">Search Engine (SEO) Settings</p>
            <label className="block">
              <span className={LABEL_CLASS}>SEO Title (leave blank to auto-generate from Name)</span>
              <input
                data-testid="admin-field-seo-title"
                value={form.seoTitle}
                onChange={(e) => handleField("seoTitle", e.target.value)}
                maxLength={70}
                className={FIELD_CLASS}
                placeholder={`${form.name} Aluminium Canopy · Bushtech NZ`}
              />
              <p className="text-white/40 text-xs mt-1">{form.seoTitle.length}/70 characters</p>
            </label>
            <label className="block">
              <span className={LABEL_CLASS}>Meta Description (leave blank to auto-generate from Intro)</span>
              <textarea
                data-testid="admin-field-seo-description"
                rows={2}
                value={form.seoDescription}
                onChange={(e) => handleField("seoDescription", e.target.value)}
                maxLength={170}
                className={FIELD_CLASS}
                placeholder="Shown under the page title in Google search results"
              />
              <p className="text-white/40 text-xs mt-1">{form.seoDescription.length}/170 characters</p>
            </label>
          </div>
        )}

        <FeaturesSection
          form={form} newFeature={newFeature} setNewFeature={setNewFeature} addFeature={addFeature}
          removeFeature={removeFeature} updateFeature={updateFeature} usingDefaultFeatures={usingDefaultFeatures}
          onResetDefault={() => { setForm((f) => ({ ...f, features: [...WELLSIDE_FEATURES] })); setUsingDefaultFeatures(true); }}
        />

        <OptionGroupsSection
          isNew={isNew} form={form} optionGroupSensors={optionGroupSensors} combinations={combinations}
          findComboEntry={findComboEntry} toggleComboStock={toggleComboStock} addOptionGroup={addOptionGroup}
          removeOptionGroup={removeOptionGroup} updateOptionGroup={updateOptionGroup} addChoice={addChoice}
          removeChoice={removeChoice} updateChoice={updateChoice} handleOptionGroupDragEnd={handleOptionGroupDragEnd}
          handleChoiceDragEnd={handleChoiceDragEnd}
          groupPlaceholder="Group label, e.g. Configuration"
          showRequiredToggle={false}
          helpText={(
            <>
              Only needed if this canopy has multiple purchasable configurations at different prices — e.g. a group called
              "Configuration" with choices like "Solid (No Glass) — Black", "Standard Glass — Dark Grey", each with its own
              price add-on and its own In Stock toggle. Leave empty for a simple single-price canopy. Every group you add
              here must be chosen from before Buy Now activates — for an optional add-on (e.g. Central Locking), include a
              $0 "None" choice so customers can decline it instead of marking the group not required.
            </>
          )}
          comboHelpText={(
            <>
              You have multiple option groups — some combinations might not be in stock independently of others
              (e.g. Black is in stock with or without glass, but Dark Grey is only in stock with glass). Untick any
              combination that customers should NOT be able to buy right now — they'll see a warning and Buy Now will
              be blocked for that specific combination, even though the individual Colour/Glass choices stay selectable.
            </>
          )}
        />

        {!isNew && (
          <SpecsSection
            form={form} newSpecKey={newSpecKey} setNewSpecKey={setNewSpecKey} newSpecValue={newSpecValue}
            setNewSpecValue={setNewSpecValue} addSpec={addSpec} removeSpec={removeSpec} updateSpec={updateSpec}
          />
        )}

        <ContentSectionsSection
          isNew={isNew} form={form} addContentSection={addContentSection} removeContentSection={removeContentSection}
          updateContentSection={updateContentSection} moveContentSection={moveContentSection}
          addSectionBullet={addSectionBullet} removeSectionBullet={removeSectionBullet}
          updateSectionBullet={updateSectionBullet}
        />

        {!isNew && (
          <div>
            <span className={LABEL_CLASS}>Photo Gallery — drag to reorder, tag icon to edit alt text</span>
            <div className="mt-2">
              <GalleryManager gallery={gallery} onChange={handleGalleryChange} onUpload={handleUpload} uploading={uploading} allowVideo />
            </div>
          </div>
        )}

        {!isNew && (
          <div>
            <span className={LABEL_CLASS}>Hero / Banner Image — the large background image at the top of this canopy's page (independent of gallery order/thumbnail)</span>
            <div className="mt-2 flex flex-wrap gap-3">
              {heroPickerGallery.map((g, i) => (
                <button
                  key={g.src}
                  type="button"
                  data-testid={`admin-hero-picker-${i}`}
                  onClick={() => handleSetHero(g.src)}
                  title={form.hero === g.src ? "Current hero image" : "Set as hero image"}
                  className={`relative w-24 h-24 overflow-hidden border-2 transition-colors ${form.hero === g.src ? "border-[var(--brand-primary)]" : "border-[var(--border-default)] hover:border-white/40"}`}
                >
                  <img src={g.src} alt="" className="w-full h-full object-cover" />
                  {form.hero === g.src && (
                    <span className="absolute inset-x-0 bottom-0 bg-[var(--brand-primary)] text-black text-[9px] font-mono uppercase tracking-wider text-center py-0.5">Hero</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {!isNew && form.thumb && (
          <div className="space-y-6">
            <div>
              <span className={LABEL_CLASS}>Desktop Focal Point — where the hero image centers on desktop/laptop screens</span>
              <div className="mt-2">
                <FocalPointPicker
                  imageSrc={form.hero || form.thumb}
                  value={form.heroPosition}
                  onChange={handleSetHeroPosition}
                  instructionText="Click the image to set the desktop focal point"
                  previewLabel="Desktop preview"
                  previewAspectClass="aspect-video"
                  previewWidthClass="w-48"
                  testIdPrefix="admin-focal-point-desktop"
                />
              </div>
            </div>
            <div>
              <span className={LABEL_CLASS}>Mobile Focal Point — where the hero image centers on phone screens (desktop crop is unaffected)</span>
              <div className="mt-2">
                <FocalPointPicker
                  imageSrc={form.hero || form.thumb}
                  value={form.heroFocalMobile}
                  onChange={handleSetHeroFocal}
                  testIdPrefix="admin-focal-point-mobile"
                />
              </div>
            </div>
          </div>
        )}

        <button data-testid="admin-save-button" type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
        </button>
      </form>
    </div>
  );
}
